const idr = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 });
const idrDecimals = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 });
const num = new Intl.NumberFormat('id-ID');
const dateShort = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short' });
const dateLong = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
const dateTitle = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
const dateCompact = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
const monthShort = new Intl.DateTimeFormat('id-ID', { month: 'short' });

export function formatIDR(value) {
  if (value == null || Number.isNaN(Number(value))) return 'Rp 0';
  return `Rp ${idr.format(Number(value))}`;
}

export function formatIDRDecimals(value) {
  if (value == null || Number.isNaN(Number(value))) return 'Rp 0';
  return `Rp ${idrDecimals.format(Number(value))}`;
}

export function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return '0';
  return num.format(Number(value));
}

/**
 * Thousands-group a numeric value for an editable money input (id-ID locale,
 * e.g. 2900 -> "2.900"). Stored values stay numeric; only the display is grouped.
 */
export function groupDigits(value) {
  if (value == null || value === '') return '';
  const n = Number(String(value).replace(/[^0-9]/g, ''));
  if (Number.isNaN(n)) return '';
  return n.toLocaleString('id-ID');
}

/**
 * "Min. Order" label from a pack quantity + unit. Handles legacy values
 * that already embed the unit (e.g. `'40 pcs'`) without duplicating it.
 * @param {string|number|null|undefined} packQty
 * @param {string|null|undefined} unit
 * @returns {string}
 */
export function minOrderLabel(packQty, unit) {
  const qty = String(packQty ?? '').trim();
  const u = String(unit ?? '').trim();
  if (!qty && !u) return '';
  if (!qty) return u;
  if (!u) return qty;
  if (qty.toLowerCase().endsWith(u.toLowerCase())) return qty;
  return `${qty} ${u}`;
}

export function parsePrice(raw) {
  const n = Number(String(raw).replace(/[^0-9]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function parseISO(dateStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function toISODate(date) {
  const d = date instanceof Date ? date : new Date(date);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function addDays(date, days) {
  const d = date instanceof Date ? new Date(date) : parseISO(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function today() {
  return new Date();
}

export function formatDayLabel(dateStr) {
  return dateShort.format(parseISO(dateStr));
}

export function formatLongDate(dateStr) {
  return dateLong.format(parseISO(dateStr));
}

export function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return dateLong.format(d);
}

/** Compact date label, e.g. "19 Sep 26" — for tight UI like picker triggers. */
export function formatPickerDate(dateStr) {
  return dateCompact.format(parseISO(dateStr));
}

export function formatDateTime(value) {
  if (value == null || value === '') return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const date = dateLong.format(d);
  const time = d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

export function formatTitleDate(dateStr) {
  return dateTitle.format(parseISO(dateStr));
}

export function formatMonthLabel(date) {
  return monthShort.format(date instanceof Date ? date : parseISO(date));
}

export function clampDateToRange(dateStr, startStr, endStr) {
  if (dateStr < startStr) return startStr;
  if (dateStr > endStr) return endStr;
  return dateStr;
}
